import {
  Button,
  Inline,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeaderCell,
  TableRow,
  Text,
} from "@softmax/design-system";
import { colors, spacing } from "@softmax/design-system/tokens.stylex";
import * as stylex from "@stylexjs/stylex";
import { Fragment, useState } from "react";
import type { PolicyVersionStatusEvent } from "../policy-version-records";

export function PolicyStatusHistory({
  events,
  onOpenSource,
}: {
  readonly events: readonly PolicyVersionStatusEvent[];
  readonly onOpenSource: (key: string) => void;
}) {
  const [expanded, setExpanded] = useState<ReadonlySet<string>>(new Set());
  return (
    <div {...stylex.props(styles.history)}>
      <Table density="compact">
        <TableHead>
          <TableRow>
            <TableHeaderCell>Time</TableHeaderCell>
            <TableHeaderCell>League</TableHeaderCell>
            <TableHeaderCell>Reason</TableHeaderCell>
            <TableHeaderCell>Change</TableHeaderCell>
            <TableHeaderCell>Sources</TableHeaderCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {events.map((event) => {
            const open = expanded.has(event.id);
            return (
              <Fragment key={event.id}>
                <TableRow>
                  <TableCell>{event.time}</TableCell>
                  <TableCell>{event.leagueName}</TableCell>
                  <TableCell>{event.reason}</TableCell>
                  <TableCell>
                    <Stack width="auto" gap={4}>
                      {event.changes.map((change) => (
                        <Text key={change}>{change}</Text>
                      ))}
                    </Stack>
                  </TableCell>
                  <TableCell>
                    {event.sources.length === 0 ? (
                      <Text tone="muted">No source records</Text>
                    ) : (
                      <Button
                        size="sm"
                        variant="quiet"
                        aria-expanded={open}
                        aria-label={`Source records for ${event.leagueName} at ${event.time}`}
                        onClick={() =>
                          setExpanded((current) => {
                            const next = new Set(current);

                            if (open) next.delete(event.id);
                            else next.add(event.id);

                            return next;
                          })
                        }
                      >
                        {open ? "Hide" : "Show"} {event.sources.length} records
                      </Button>
                    )}
                  </TableCell>
                </TableRow>
                {open && (
                  <TableRow>
                    <TableCell colSpan={5}>
                      <Stack width="auto">
                        {event.sources.map((source) => (
                          <div
                            key={source.key}
                            {...stylex.props(styles.source)}
                          >
                            <Stack width="auto" gap={4}>
                              <Inline>
                                <Text size="small" tone="muted">
                                  {source.kind}
                                </Text>
                                {source.openable ? (
                                  <Button
                                    size="sm"
                                    variant="quiet"
                                    onClick={() => onOpenSource(source.key)}
                                  >
                                    {source.title}
                                  </Button>
                                ) : (
                                  <Text>{source.title}</Text>
                                )}
                                {source.recordId && (
                                  <Text size="label">{source.recordId}</Text>
                                )}
                              </Inline>
                              {source.summary && (
                                <Text size="small" tone="muted">
                                  {source.summary}
                                </Text>
                              )}
                            </Stack>
                          </div>
                        ))}
                      </Stack>
                    </TableCell>
                  </TableRow>
                )}
              </Fragment>
            );
          })}
        </TableBody>
      </Table>
    </div>
  );
}

const styles = stylex.create({
  history: { minWidth: "680px" },
  source: {
    borderInlineStartWidth: 2,
    borderInlineStartStyle: "solid",
    borderInlineStartColor: colors.border,
    paddingInlineStart: spacing.space12,
  },
});
